const API_BASE = process.env.REACT_APP_AUTH_API_URL || 'https://api.datacircle.ru';

export const ACCESS_TOKEN_KEY = 'dc_auth_access_token';
export const REFRESH_TOKEN_KEY = 'dc_auth_refresh_token';
const REFRESH_LOCK_KEY = 'dc_auth_refresh_lock';
const REFRESH_LOCK_TTL_MS = 15_000;

export const AUTH_SESSION_EXPIRED_EVENT = 'dc:auth-session-expired';
export const AUTH_TOKENS_UPDATED_EVENT = 'dc:auth-tokens-updated';

export type AuthTokens = {
  accessToken: string;
  refreshToken: string;
};

type RefreshApiResponse = {
  accessToken: string;
  refreshToken: string;
};

let refreshInFlight: Promise<boolean> | null = null;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

export function setAuthTokens(tokens: AuthTokens): void {
  localStorage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
  window.dispatchEvent(new CustomEvent(AUTH_TOKENS_UPDATED_EVENT, {
    detail: { accessToken: tokens.accessToken },
  }));
}

export function clearAuthTokens(): void {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem(REFRESH_LOCK_KEY);
}

export function getAuthHeader(): Record<string, string> | null {
  const token = getAccessToken();
  if (!token) return null;
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

export function isAccessTokenError(status: number, errorMessage: string): boolean {
  if (status !== 401) return false;
  const msg = errorMessage.toLowerCase();
  return msg.includes('invalid access token')
    || msg.includes('missing bearer token');
}

export function notifySessionExpired(): void {
  clearAuthTokens();
  window.dispatchEvent(new Event(AUTH_SESSION_EXPIRED_EVENT));
}

function isRefreshLockActive(): boolean {
  const raw = localStorage.getItem(REFRESH_LOCK_KEY);
  if (!raw) return false;
  const startedAt = Number(raw);
  if (!Number.isFinite(startedAt)) return false;
  return Date.now() - startedAt < REFRESH_LOCK_TTL_MS;
}

async function waitForOtherTabRefresh(previousAccess: string | null): Promise<boolean> {
  const deadline = Date.now() + REFRESH_LOCK_TTL_MS;
  while (Date.now() < deadline) {
    const access = getAccessToken();
    if (access && access !== previousAccess) return true;
    if (!isRefreshLockActive()) break;
    await sleep(200);
  }
  return Boolean(getAccessToken() && getAccessToken() !== previousAccess);
}

async function performRefreshRequest(refreshToken: string): Promise<AuthTokens | null> {
  const response = await fetch(`${API_BASE}/auth/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refreshToken }),
  });
  if (!response.ok) return null;
  const data = (await response.json()) as RefreshApiResponse;
  if (!data.accessToken || !data.refreshToken) return null;
  return { accessToken: data.accessToken, refreshToken: data.refreshToken };
}

/** Обновляет access/refresh токены. Один refresh на вкладку; учитывает параллельный refresh в других вкладках. */
export async function refreshAuthTokens(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight;

  refreshInFlight = (async () => {
    const accessBefore = getAccessToken();

    if (isRefreshLockActive()) {
      const refreshedByOtherTab = await waitForOtherTabRefresh(accessBefore);
      if (refreshedByOtherTab) return true;
    }

    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      notifySessionExpired();
      return false;
    }

    localStorage.setItem(REFRESH_LOCK_KEY, String(Date.now()));
    try {
      const tokens = await performRefreshRequest(refreshToken);
      if (tokens) {
        setAuthTokens(tokens);
        return true;
      }

      await sleep(400);
      const accessAfter = getAccessToken();
      if (accessAfter && accessAfter !== accessBefore) return true;

      notifySessionExpired();
      return false;
    } finally {
      localStorage.removeItem(REFRESH_LOCK_KEY);
    }
  })();

  try {
    return await refreshInFlight;
  } finally {
    refreshInFlight = null;
  }
}

/** Синхронизация токенов между вкладками через storage events. */
export function initAuthTokenCrossTabSync(): () => void {
  const onStorage = (event: StorageEvent) => {
    if (event.key !== ACCESS_TOKEN_KEY && event.key !== REFRESH_TOKEN_KEY) return;

    if (event.key === ACCESS_TOKEN_KEY && event.newValue) {
      window.dispatchEvent(new CustomEvent(AUTH_TOKENS_UPDATED_EVENT, {
        detail: { accessToken: event.newValue },
      }));
      return;
    }

    if (event.key === ACCESS_TOKEN_KEY && !event.newValue) {
      window.dispatchEvent(new Event(AUTH_SESSION_EXPIRED_EVENT));
    }
  };

  window.addEventListener('storage', onStorage);
  return () => window.removeEventListener('storage', onStorage);
}

export { API_BASE as AUTH_API_BASE };
